import React from 'react';
import ReactDOM from 'react-dom/client';
import { Mail, MapPin, Globe, Calendar, Briefcase, Code, Users, Award, BookOpen } from 'lucide-react';
import './index.css';

const CV = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header Section */}
        <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-8 py-10">
          <h1 className="text-4xl font-bold mb-2">Mykola Bashmakov</h1>
          <p className="text-xl mb-4 opacity-95">Engineering Lead · AI/LLM Platforms · Cloud & Edge · Kubernetes</p>
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Ukraine</span>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span>English, Ukrainian</span>
            </div>
          </div>
          <p className="mt-4 text-sm opacity-90">
            <strong>Focus:</strong> Building and leading teams to ship AI-powered products—reliable backends, real-time systems, and cloud/edge infra.
          </p>
        </header>

        {/* Main Content */}
        <div className="px-8 py-6">
          {/* Summary Section */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              Summary
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Engineering Lead with a strong full-stack IC backbone (Python/FastAPI, React/TypeScript, streaming, data pipelines) who scales teams, systems, and delivery. 
              I set product-aligned roadmaps, introduce sensible engineering process (GitOps, CI/CD, SLOs), and drive measurable outcomes: 
              latency, reliability, and cost. Former Tech Lead for multi-service AI systems across AWS/Kubernetes and edge deployments.
            </p>
          </section>

          {/* Leadership Highlights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Leadership Highlights
            </h2>
            <div className="space-y-3">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800">Org & People</h3>
                <p className="text-gray-600 text-sm">Built cross-functional squads (backend, DevOps, ML), defined career ladders, ran hiring loops and onboarding; mentored seniors toward tech-lead roles.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800">Delivery & Process</h3>
                <p className="text-gray-600 text-sm">Roadmaps, quarterly planning, trunk-based development with CI checks, "you build it—you run it," on-call with clear escalation; DRI model and RFCs.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800">Quality & Reliability</h3>
                <p className="text-gray-600 text-sm">SLOs/SLIs, runbooks, incident reviews; shift-left testing (unit/integration/e2e), contract tests for services; progressive delivery via GitOps.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800">Architecture Ownership</h3>
                <p className="text-gray-600 text-sm">Event-driven microservices (REST/gRPC/GraphQL), streaming (SSE/WebSockets), data platforms (PostgreSQL/pgvector, Redis), GPU/edge inference.</p>
              </div>
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800">Stakeholder Management</h3>
                <p className="text-gray-600 text-sm">Translated product goals into milestones, clear success metrics, and cost/latency guardrails; partnered with ops, sales, and customer success.</p>
              </div>
            </div>
          </section>

          {/* Experience */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Experience
            </h2>
            <div className="bg-gray-50 rounded-lg p-5">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Go Wombat</h3>
                  <p className="text-gray-600 font-medium">Team Lead / Backend Tech Lead</p>
                </div>
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Feb 2021 – present
                </span>
              </div>
              <p className="text-gray-700 mb-3">Led platform initiatives for AI-driven inspection and related products.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Scaled from single-team delivery to <strong>squads</strong> with shared platform libraries, golden paths, and CI templates; cut cycle time ~30–40%.</span>
                </li>
                <li className="flex">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Drove <strong>Kubernetes + GitOps</strong> adoption (Helm, Argo CD, Rancher), blue/green and canaries; error budget–based releases.</span>
                </li>
                <li className="flex">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Owned cross-service <strong>API strategy</strong> (REST/GraphQL/gRPC), <strong>Kafka</strong> eventing, and <strong>SingleStore</strong> analytics; standardized observability (Prometheus/Grafana, Sentry).</span>
                </li>
                <li className="flex">
                  <span className="text-blue-600 mr-2">•</span>
                  <span>Balanced IC work on critical paths: performance tuning, streaming APIs, and GPU warm-up/throughput fixes for inference services.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Selected Projects */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Code className="w-5 h-5 text-blue-600" />
              Selected Projects (Lead Impact)
            </h2>
            <div className="space-y-4">
              {/* ButlerIQ */}
              <div className="border rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-1">ButlerIQ – LLM-powered hotel room assistant (2025)</h3>
                <p className="text-sm text-gray-600 mb-2">Voice-activated IoT concierge system transforming hotel guest experience. Guests use natural language to order room service, request amenities, control room features, get local recommendations, and communicate with hotel staff. Real-time staff dashboard for order management, fulfillment tracking, and analytics. Multi-property support with centralized management and property-specific customization.</p>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong className="text-gray-700">Role:</strong> Engineering Lead / Principal IC for backend architecture and implementation.</p>
                  <p><strong className="text-gray-700">Scope:</strong> Product requirements gathering → system architecture design → MVP development → production deployment with comprehensive SLOs → ongoing optimization.</p>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Stack:</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">FastAPI</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">PostgreSQL</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">Redis</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">OpenAI GPT-4</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">ElevenLabs TTS</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">Whisper STT</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md font-medium">Home Assistant</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md font-medium">SSE/WebSockets</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-md font-medium">Docker</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-md font-medium">Kubernetes</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">Prometheus</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Technical Achievements:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Sub-200ms voice-to-response processing</li>
                      <li>Multi-tenant isolation with row-level security</li>
                      <li>Intelligent rate-limiting per room/hotel/property</li>
                      <li>Comprehensive audit logging for compliance</li>
                      <li>WebSocket connection pooling for thousands of concurrent rooms</li>
                      <li>Fallback mechanisms for LLM failures</li>
                      <li>99.9% uptime SLA</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Key Features:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Multilingual support (15+ languages)</li>
                      <li>Context-aware conversations with session memory</li>
                      <li>Smart routing to appropriate hotel departments</li>
                      <li>Integration with PMS/POS systems</li>
                      <li>Real-time translation for international guests</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Impact:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Deployed across 500+ rooms in 3 hotels</li>
                      <li>40% reduction in front desk calls</li>
                      <li>Average response time under 5 seconds</li>
                      <li>Handling 10K+ daily interactions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Cybord */}
              <div className="border rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-1">Cybord – AI for SMT component quality inspection (Israel)</h3>
                <p className="text-sm text-gray-600 mb-2">Enterprise-grade computer vision platform for electronics manufacturing quality control. Real-time defect detection in SMT (Surface Mount Technology) production lines, processing millions of component images daily with sub-millisecond inference latency. System handles various defect types: component placement, soldering quality, missing components, polarity issues, and damage detection.</p>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong className="text-gray-700">Role:</strong> Backend engineer → Team Lead (team of 8 engineers: 4 backend, 2 ML, 1 DevOps, 2 frontend, 2 QA).</p>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Architecture:</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-md font-medium">C++</span>
                      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-md font-medium">Python</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">Kubernetes</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">Envoy</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">Traefik</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md font-medium">Kafka</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">AWS EKS</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">ECS</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">Fargate</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">CloudFront</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">SQS</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">SNS</span>
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-md font-medium">Grafana</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">Tailscale</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">Multi-region (US/EU/APAC)</span>
                      <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-md font-medium">Edge Computing</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-md font-medium">GPU Clusters</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">What I led:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Complete GitOps transformation using ArgoCD</li>
                      <li>GPU cluster optimization achieving 3x throughput improvement through better batching and model quantization</li>
                      <li>API standardization across services (OpenAPI specs)</li>
                      <li>Complex EMS/OEM integrations</li>
                      <li>Data pipeline optimization reducing storage costs by 40%</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Technical Challenges Solved:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>GPU memory management for running multiple concurrent models</li>
                      <li>Achieving sub-100ms P99 latency for inference</li>
                      <li>Handling 10TB+ daily image data with intelligent compression</li>
                      <li>Zero-downtime deployments with blue-green strategy</li>
                      <li>Real-time model updates without service interruption</li>
                      <li>Handling varying image qualities and lighting conditions</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Innovation:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Implemented adaptive quality thresholds based on production context</li>
                      <li>Developed custom ONNX runtime optimizations</li>
                      <li>Built automated retraining pipeline triggered by drift detection</li>
                      <li>Created synthetic data generation for rare defect types</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Outcomes:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>70% reduction in false positives through ensemble methods</li>
                      <li>95% defect detection accuracy</li>
                      <li>Processing 5M+ components daily</li>
                      <li>15 enterprise customers onboarded</li>
                      <li>System scaled to handle 100+ production lines simultaneously</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* HR Platform */}
              <div className="border rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-1">HR Management Platform (NDA) – Enterprise SaaS Solution</h3>
                <p className="text-sm text-gray-600 mb-2">Comprehensive HRIS (Human Resource Information System) supporting organizations with 5000+ employees. Full employee lifecycle management from recruiting and onboarding through performance management, time tracking, leave management, and offboarding. Designed for complex organizational structures with multiple departments, locations, and reporting hierarchies.</p>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong className="text-gray-700">Role:</strong> Lead architect and delivery manager (team of 6: 2 backend, 2 frontend, 1 DevOps, 1 QA).</p>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Stack:</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">FastAPI</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">PostgreSQL</span>
                      <span className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded-md font-medium">React</span>
                      <span className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded-md font-medium">TypeScript</span>
                      <span className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded-md font-medium">Redux Toolkit</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">Redis</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md font-medium">Celery</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">AWS ECS</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">RDS Multi-AZ</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">S3</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md font-medium">CloudFront</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Key Features Implemented:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Multi-tenant architecture with complete data isolation</li>
                      <li>Granular RBAC with 20+ permission levels and custom roles</li>
                      <li>GDPR/CCPA compliant with data retention policies</li>
                      <li>Advanced reporting with scheduled delivery</li>
                      <li>Employee self-service portal</li>
                      <li>Manager dashboards with team analytics</li>
                      <li>Automated onboarding workflows with task assignments</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Technical Highlights:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Implemented CQRS pattern for read/write optimization</li>
                      <li>Built custom ORM extensions for complex queries</li>
                      <li>Developed audit trail system tracking all data changes</li>
                      <li>Created template engine for customizable documents</li>
                      <li>Implemented SSO with SAML 2.0 and OAuth</li>
                      <li>Built real-time notification system using WebSockets</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Performance & Security:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Horizontal scaling to handle 50K API requests/minute</li>
                      <li>Sub-100ms response time for critical endpoints</li>
                      <li>Encrypted PII at rest and in transit</li>
                      <li>Automated security scanning in CI/CD</li>
                      <li>Penetration testing passed with zero critical findings</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Results:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>MVP delivered in 8 weeks</li>
                      <li>99.95% uptime over 12 months</li>
                      <li>Zero security incidents</li>
                      <li>Reduced HR operational overhead by 35%</li>
                      <li>Successful SOC 2 Type II certification achieved</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Botanical Garden */}
              <div className="border rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-1">Botanical Garden AI Assistant – Interactive Visitor Experience Platform</h3>
                <p className="text-sm text-gray-600 mb-2">Multilingual conversational AI system serving 10K+ daily visitors across mobile app and kiosk touchpoints. Combines computer vision for plant identification, RAG for botanical knowledge, and LLM for natural conversations. Provides personalized tour recommendations, educational content, event information, and accessibility guidance. Supports multiple languages with cultural context awareness.</p>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong className="text-gray-700">Role:</strong> Lead backend engineer and ML infrastructure architect, working closely with botanical experts and UX team.</p>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Stack:</p>
                    <div className="flex flex-wrap gap-1.5">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">FastAPI</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">PostgreSQL</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">pgvector</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">OpenAI GPT-4</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md font-medium">Claude</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md font-medium">LangGraph</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md font-medium">Redis</span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-md font-medium">MinIO</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">AI Features:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>RAG system with 50K+ botanical documents and scientific papers</li>
                      <li>Context-aware conversations maintaining visitor history</li>
                      <li>Personalized recommendations based on interests</li>
                      <li>Seasonal content updates</li>
                      <li>Endangered species awareness module</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Technical Implementation:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Hybrid search combining keyword and semantic search</li>
                      <li>Incremental learning for new plant varieties</li>
                      <li>A/B testing framework for conversation flows</li>
                      <li>Prompt optimization reducing token usage by 40%</li>
                      <li>Caching strategy for common queries</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Innovation:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Bring LLM conversation experience to the platform</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-1">Impact:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2 text-gray-600">
                      <li>Increased guided tour bookings</li>
                      <li>Improved users experience and engagement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Technical Strategy & Toolkit */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Technical Strategy & Toolkit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Platforms</h3>
                <p className="text-xs text-gray-600">Kubernetes (k3s/EKS), Helm, Argo CD, Rancher; Terraform; Cloudflare.</p>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Cloud/Data</h3>
                <p className="text-xs text-gray-600">AWS (EC2/ECS/Fargate/S3/SQS/SNS/CloudFront/Secrets), PostgreSQL/pgvector, Redis, SingleStore, Kafka.</p>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Backend/Realtime</h3>
                <p className="text-xs text-gray-600">Python (FastAPI/Django/asyncio), REST/GraphQL/gRPC, SSE/WebSockets, Celery.</p>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Frontend</h3>
                <p className="text-xs text-gray-600">React, TypeScript, shadcn/ui, MUI, Tailwind CSS, CSS3, ESLint/Prettier, CI/CD pipelines, Webpack/Vite, Next.js.</p>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">AI/LLM</h3>
                <p className="text-xs text-gray-600">OpenAI API, embeddings, RAG, LangGraph/LangChain/pydanticAI; evals/AB via LangFuse.</p>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Observability</h3>
                <p className="text-xs text-gray-600">Prometheus/Grafana, Sentry; structured logging & tracing (OTel).</p>
              </div>
              <div className="bg-blue-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 text-sm mb-1">Quality</h3>
                <p className="text-xs text-gray-600">Pytest unit/integration/e2e, contract tests, load tests; pre-commit, type checks.</p>
              </div>
            </div>
          </section>

          {/* Management Practices */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Management Practices</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-1">Planning</h3>
                <p className="text-xs text-gray-600">OKRs → quarterly themes → milestones; explicit risk registers.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-1">Developer Experience</h3>
                <p className="text-xs text-gray-600">DX scorecards, paved roads, reusable service templates, internal docs.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-1">Reliability</h3>
                <p className="text-xs text-gray-600">SLOs with alerts, incident command, blameless postmortems, runbooks.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700 text-sm mb-1">Security & Compliance</h3>
                <p className="text-xs text-gray-600">Secrets management, least privilege, CIS-leaning baselines, audit logs.</p>
              </div>
            </div>
          </section>

          {/* Education */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Education
            </h2>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-800">Taras Shevchenko National University of Kyiv</h3>
                <p className="text-gray-600">Computer Science & Cybernetics</p>
                <p className="text-sm text-gray-500">2019–2022</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-800">Zaporizhzhia National University</h3>
                <p className="text-gray-600">Bachelor's Degree in Computer Science</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="border-t pt-4 text-center">
            <p className="text-sm text-gray-600 italic">References and detailed case studies available on request.</p>
          </footer>
        </div>
      </div>
    </div>
  );
};

// Render the CV component to the DOM
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <CV />
  </React.StrictMode>
);

export default CV;